import { batchUpdateCareMessagesAPI, resendCareMessageAPI } from '@/api/care';
import {
  careStatusOptions, careStatusColor, careStatusLabel,
  canResendCareMessage, canCancelCareMessage, careEditStatusOptions, careEditTimeDisabled, careResendBlockMessage,
} from '@/utils/careScheduleStatus';

// 同一頁的列表／約診小卡共享；切换約診卸載元件不能丟失尚未確認的操作。
const resendIntents = {};

function operationId() {
  // 無安全亂數時停止提交，不用時間或 Math.random 代替操作唯一鍵。
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function errorReason(error) {
  const data = error.response && error.response.data;
  const errors = data && data.errors && Object.values(data.errors).flat();
  return (errors && errors.find(message => typeof message === 'string'))
    || (data && typeof data.message === 'string' && data.message)
    || (data && typeof data.messages === 'string' && data.messages)
    || '無法確認排程更新結果。';
}

// 使用此流程的列表須提供 loading 與 fetchData()；更新後一律重讀該列表。
export default {
  data() {
    return {
      saving: false, editDialog: false, currentItem: null, editableMessages: [],
      datePickerMenus: [], minDate: '', statusOptions: careStatusOptions,
      editOwner: null, resendIntents,
    };
  },
  methods: {
    isEditable(item) {
      return (item.messages || []).some(msg => this.isMessageEditable(msg) || Boolean(this.resendIntents[this.intentKey(msg.id)]));
    },
    isMessageEditable(msg) {
      return canCancelCareMessage(msg.send_status) || canResendCareMessage(msg);
    },
    statusOptionsFor: careEditStatusOptions,
    isTimeDisabled(msg) { return careEditTimeDisabled(msg, this.saving); },
    statusColor: careStatusColor,
    formatStatus: careStatusLabel,
    intentKey(id) { return `${JSON.stringify(this.axiosParamData)}:${id}`; },
    openEditDialog(item) {
      if (this.loading || this.saving || this.readOnly) return;
      this.currentItem = item;
      this.editOwner = { ...this.axiosParamData };
      this.minDate = this.$moment().tz('Asia/Taipei').format('YYYY-MM-DD');
      this.editableMessages = item.messages.map((msg, index) => {
        const intent = this.resendIntents[this.intentKey(msg.id)] || null;
        const taipeiTime = this.$moment(intent ? intent.scheduledSendAt : msg.scheduled_send_at).tz('Asia/Taipei');
        const originalTime = this.$moment(msg.scheduled_send_at).tz('Asia/Taipei');
        return {
          id: msg.id, title: msg.title || `細項 ${index + 1}`,
          date: taipeiTime.format('YYYY-MM-DD'), hour: taipeiTime.format('HH'), minute: taipeiTime.format('mm'),
          status: String(msg.send_status), originalStatus: String(msg.send_status),
          originalTime: originalTime.format('YYYY-MM-DDTHH:mm'), originalScheduledSendAt: msg.scheduled_send_at,
          canResend: canResendCareMessage(msg), canCancel: canCancelCareMessage(msg.send_status),
          resendBlockReason: careResendBlockMessage(msg.resend_block_reason),
          operation: canResendCareMessage(msg) || intent ? 'resend' : 'edit',
          isPotentiallyEditable: this.isMessageEditable(msg) || Boolean(intent),
          isEditing: Boolean(intent && !intent.result),
          resendIntent: intent, resendResult: intent && intent.result,
          saveError: '', batchSubmitted: false,
        };
      });
      this.datePickerMenus = Array(item.messages.length).fill(false);
      this.editDialog = true;
    },
    toggleEditMessage(msg) {
      if (this.saving || !msg.isPotentiallyEditable || msg.resendResult || msg.batchSubmitted
        || (msg.resendIntent && msg.resendIntent.uncertain)) return;
      msg.isEditing = !msg.isEditing;
    },
    closeEditDialog() {
      if (this.saving) return;
      // 未確認的請求保留到同頁再次開啟；不能因取消視窗就換 UUID 重送。
      const hasUnresolved = this.editableMessages.some(msg => msg.resendIntent && !msg.resendIntent.result);
      this.editableMessages.forEach(msg => {
        if (!hasUnresolved && msg.resendIntent) {
          this.$delete(this.resendIntents, `${JSON.stringify(this.editOwner)}:${msg.id}`);
        }
      });
      this.editDialog = false;
      this.currentItem = null;
      this.editableMessages = [];
      this.datePickerMenus = [];
      this.editOwner = null;
    },
    async saveChanges() {
      if (this.saving || !this.editDialog || this.readOnly) return;
      if (JSON.stringify(this.editOwner) !== JSON.stringify(this.axiosParamData)) {
        this.$notify.warning('帳號已切換，請重新開啟排程。');
        return;
      }
      const updates = [], resendRows = [];
      for (const msg of this.editableMessages) {
        if (!msg.isEditing || !msg.isPotentiallyEditable || msg.resendResult || msg.batchSubmitted) continue;
        const uncertain = msg.resendIntent && msg.resendIntent.uncertain;
        if (uncertain) { resendRows.push(msg); continue; }
        const isResend = msg.operation === 'resend';
        const statusChanged = msg.status !== msg.originalStatus;
        const localTime = `${msg.date}T${msg.hour}:${msg.minute}`;
        const timeChanged = !this.isTimeDisabled(msg) && localTime !== msg.originalTime;
        if (!isResend && !statusChanged && !timeChanged) continue;
        if ((isResend && !msg.canResend) || (!isResend && !this.statusOptionsFor(msg).some(option => option.value === msg.status))) {
          this.$notify.warning('請選擇可用的排程操作。');
          return;
        }
        const sendTime = timeChanged
          ? this.$moment.tz(localTime, 'YYYY-MM-DDTHH:mm', true, 'Asia/Taipei')
          : this.$moment(msg.originalScheduledSendAt);
        if (!/^[1-9]\d*$/.test(String(msg.id)) || !/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/.test(msg.originalScheduledSendAt || '') || !sendTime.isValid()) {
          this.$notify.warning('排程 ID 或發送時間無效，請重新查詢。');
          return;
        }
        if (timeChanged && (!/^\d{2}$/.test(msg.minute) || Number(msg.minute) % 5 !== 0)) {
          this.$notify.warning('發送時間請使用五分鐘刻度。');
          return;
        }
        if ((isResend || timeChanged || (statusChanged && msg.status === '0')) && !sendTime.isAfter(this.$moment())) {
          this.$notify.warning('請將發送時間設定為有效的未來時間。');
          return;
        }
        const scheduledSendAt = timeChanged ? sendTime.utc().format() : msg.originalScheduledSendAt;
        if (isResend) {
          try {
            const intent = msg.resendIntent || { requestId: operationId(), scheduledSendAt, uncertain: false, result: null };
            intent.scheduledSendAt = scheduledSendAt;
            msg.resendIntent = intent;
            this.$set(this.resendIntents, this.intentKey(msg.id), intent);
          } catch (error) {
            this.$notify.error('無法建立安全的排程操作識別碼，請使用支援的瀏覽器。');
            return;
          }
          resendRows.push(msg);
        } else {
          updates.push({ id: msg.id, scheduled_send_at: scheduledSendAt, send_status: timeChanged ? 0 : Number(msg.status) });
        }
      }
      if (!updates.length && !resendRows.length) {
        this.$notify.warning('沒有已變更的項目可儲存。');
        return;
      }

      this.saving = true;
      let unresolved = false;
      try {
        if (updates.length) {
          try {
            await batchUpdateCareMessagesAPI({ updates });
            this.$notify.success('排程已更新。');
          } catch (error) {
            this.$notify.error(`${errorReason(error)} 部分項目可能已更新，請依重新查詢的結果確認。`);
          } finally {
            // 普通 batch 沒有操作鍵；一旦提交就只回查，不從舊彈窗再次送出。
            this.editableMessages.filter(msg => updates.some(update => update.id === msg.id)).forEach(msg => {
              msg.batchSubmitted = true; msg.isEditing = false;
            });
          }
        }
        for (const msg of resendRows) {
          const intent = msg.resendIntent;
          intent.uncertain = true;
          msg.saveError = '';
          try {
            const response = await resendCareMessageAPI(msg.id, {
              ...this.editOwner, request_id: intent.requestId, scheduled_send_at: intent.scheduledSendAt,
            });
            const result = response && response.data && response.data.data;
            if (!result || String(result.source_queue_id) !== String(msg.id) || !/^[1-9]\d*$/.test(String(result.queue_id))
              || String(result.queue_id) === String(msg.id) || typeof result.replayed !== 'boolean') throw new Error('Invalid result');
            intent.result = result; intent.uncertain = false;
            msg.resendResult = result; msg.isEditing = false;
          } catch (error) {
            const status = error.response && error.response.status;
            // 明確拒絕允許修正輸入；timeout/5xx/不完整回應固定重用原 UUID 與時間。
            if ([400, 403, 404, 409, 422].includes(status)) intent.uncertain = false;
            msg.saveError = `${errorReason(error)}${intent.uncertain ? ' 請重試確認原操作，勿另建排程。' : ''}`;
            unresolved = true;
          }
        }
        if (resendRows.some(msg => msg.resendResult)) this.$notify.success('已重新安排發送，原紀錄已保留。');
        if (unresolved) this.$notify.error('部分排程尚未確認，已保留成功結果；請查看各列說明後重試。');
        await this.fetchData();
      } finally {
        this.saving = false;
        if (!unresolved) this.closeEditDialog();
      }
    },
  },
};
